import React, {useState} from 'react';
import Button from "components/Button";
import {TrashIcon} from "@heroicons/react/solid";
import Field from "components/Form/Field";
import TriggerActionType from "types/TriggerActionType";
import Well from "components/Well";
import Select from 'components/Form/Select';
import ngage from "services/ngage";
import {Option} from "components/Form/Select/Select";
import {FormikProps} from "formik";
import {GlobeIcon} from "@heroicons/react/outline";
import {exhibitOption, facilityOption, messageOption, offerOption} from 'services/options';
import {useOperator} from "../../../context/OperatorContext";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import Alert from "../../../components/Alert";

// Types ----

export type ActionFormData = {
  id?: number
  action_type: TriggerActionType
  name: string
  order: number
  entity?: Option | string
}

type ActionEditorProps = {
  name: string
  action: ActionFormData
  index: number
  count: number
  form: FormikProps<any>
  onRemove: () => any
  onChange: (action: ActionFormData) => any
}

// Component ----

function ActionEditor({name, action, index, count, form, onRemove}: ActionEditorProps) {
  const {termFor} = useOperator();

  const [actionType, setActionType] = useState<TriggerActionType | null>(null);

  // Data sources
  const exhibitSource = async (query: string) => {
    return (await ngage.exhibits.filtered({published: true, search: query || undefined, per_page: 500}))?.data.map(exhibitOption) || []
  }

  const facilitySource = async (query: string) => {
    return (await ngage.facilities.filtered({search: query || undefined, per_page: 500}))?.data.map(facilityOption) || []
  }

  const messageSource = async (query: string) => {
    return (await ngage.messages.filtered({search: query || undefined, perPage: 500}))?.data.map(messageOption) || []
  }

  const offerSource = async (query: string) => {
    return (await ngage.offers.filtered({search: query || undefined, perPage: 500}))?.data.map(offerOption) || []
  }

  // Change handlers
  const changeType = (event: React.FormEvent<HTMLInputElement>) => {
    setActionType(event.currentTarget.value as TriggerActionType);
    
    void form.setFieldValue(`${name}.${index}.name`, '')
    void form.setFieldValue(`${name}.${index}.entity`, null)
    void form.setFieldValue(`${name}.${index}.action_type`, event.currentTarget.value)
  }

  const changeEntity = (selected: Option | string, actionName: string) => {
    void form.setFieldValue(`${name}.${index}.entity`, selected)

    if (!form.values[name][index].name) {
      void form.setFieldValue(`${name}.${index}.name`, actionName)
    }
  }

  const changeUrl = (event: React.FormEvent<HTMLInputElement>) => {
    changeEntity(event.currentTarget.value, "Navigate to URL")
  }

  // Well action
  const deleteButton = <Button
    color={Button.colors.WHITE}
    size={Button.sizes.X_SMALL}
    icon={TrashIcon}
    onClick={onRemove}
    disabled={count < 2}
  />

  const showUnsupportedAlert = actionType === 'exhibit' || actionType === 'facility' || actionType === 'url';

  return (
    <Well heading={`Action ${index + 1}`} action={deleteButton}>
      {showUnsupportedAlert && (
        <div className="mb-8">
          <Alert message={`This action is currently only supported for Beacons and QR Codes. Interaction Areas and ${termFor('Zones')} to follow.`} />
        </div>
      )}

      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
        {/* Type */}
        <div className="sm:col-span-2">
          <Field
            as="select"
            id={`${name}.${index}.action_type`}
            name={`${name}.${index}.action_type`}
            label="Action Type *"
            onChange={changeType}
          >
            <option value={TriggerActionType.UNKNOWN} disabled>Select action type...</option>
            <option value={TriggerActionType.EXHIBIT}>Show {termFor("Exhibit")}</option>
            <option value={TriggerActionType.FACILITY}>Show {termFor("Facility")}</option>
            <option value={TriggerActionType.MESSAGE}>Send Message</option>
            <option value={TriggerActionType.OFFER}>Grant Offer</option>
            <option value={TriggerActionType.URL}>Visit URL</option>
          </Field>
        </div>

        {action.action_type !== TriggerActionType.UNKNOWN && <>
          {/* Configuration */}
          <div className="sm:col-span-4">
            {action.action_type === TriggerActionType.EXHIBIT &&
              <Select
                label={`${termFor("Exhibit")} *`}
                hint="Type to search"
                source={exhibitSource}
                selected={(action.entity && typeof action.entity === 'object') ? action.entity : null}
                onChange={selected => changeEntity(selected, `Show ${termFor("exhibit")} "${selected.name}"`)}
              />
            }

            {action.action_type === TriggerActionType.FACILITY &&
              <Select
                label={`${termFor("Facility")} *`}
                hint="Type to search"
                source={facilitySource}
                selected={(action.entity && typeof action.entity === 'object') ? action.entity : null}
                onChange={selected => changeEntity(selected, `Show ${termFor("facility")} "${selected.name}"`)}
              />
            }

            {action.action_type === TriggerActionType.MESSAGE &&
              <Select
                label="Message *"
                hint="Type to search"
                source={messageSource}
                selected={(action.entity && typeof action.entity === 'object') ? action.entity : null}
                onChange={selected => changeEntity(selected, `Send message "${selected.name}"`)}
              />
            }

            {action.action_type === TriggerActionType.OFFER &&
              <Select
                label="Offer *"
                hint="Type to search"
                source={offerSource}
                selected={(action.entity && typeof action.entity === 'object') ? action.entity : null}
                onChange={selected => changeEntity(selected, `Grant offer "${selected.name}"`)}
              />
            }

            {/* @ts-ignore */}
            {(action.action_type !== TriggerActionType.URL && !!form.errors?.actions?.[index]?.entity && form.touched?.actions?.[index]?.entity) &&
              <FieldHelp isError>
                {/* @ts-ignore */}
                {form.errors.actions[index].entity}
              </FieldHelp>
            }

            {action.action_type === TriggerActionType.URL &&
              <Field
                type="text"
                id={`${name}.${index}.entity`}
                name={`${name}.${index}.entity`}
                label="URL *"
                icon={GlobeIcon}
                autoComplete="off"
                maxLength="250"
                onChange={changeUrl}
              />
            }
          </div>

          {/* Name */}
          <div className="sm:col-span-3">
            <Field
              type="text"
              id={`${name}.${index}.name`}
              name={`${name}.${index}.name`}
              label="Action Name *"
              autoComplete="off"
              maxLength="250"
            />
          </div>
        </>}

      </div>
    </Well>
  );
}

// Export ----

export default ActionEditor;